import {
	createRemoteDataSource,
	LazyLoadCursorKind,
	NumberFormatter,
	RemoteDataSourceState
} from "client/Hooks";
import { generateTrackingId } from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { VotersOptionLayout } from "./VotersOptionLayout";
import { UserList } from "./UserList";
import {
	HeightAnimationContainer,
	LoadMoreIndicator,
	NoResults
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { CommunityPostPollAnswerViewModel, listVoters } from "client/Api/Communities/communityPostViewer";

export type VotersOptionListItemProps = {
	postId: number;
	option: CommunityPostPollAnswerViewModel;
	voteCount: number;
	numberFormatter: NumberFormatter;
};

export const VotersOptionListItem = (props: VotersOptionListItemProps): ReactElement => {
	const {
		postId,
		option,
		voteCount,
		numberFormatter
	} = props;

	const trackingId: string = generateTrackingId("option", `|${option.id}`);

	return voteCount > 0
		? (
			<Option
				trackingId={trackingId}
				postId={postId}
				option={option}
				voteCount={voteCount}
				numberFormatter={numberFormatter}
			/>
		)
		: (
			<OptionWithoutVotes
				trackingId={trackingId}
				text={option.text}
				numberFormatter={numberFormatter}
			/>
		);
};
VotersOptionListItem.displayName = "VotersOptionListItem";

type OptionWithoutVotesProps = {
	trackingId: string;
	text: string;
	numberFormatter: NumberFormatter;
};

const OptionWithoutVotes = (props: OptionWithoutVotesProps): ReactElement => {
	const {
		text,
		trackingId,
		numberFormatter
	} = props;

	const [isExpanded, setIsExpanded] = useState<boolean>(false);

	return (
		<VotersOptionLayout trackingId={trackingId}>
			<VotersOptionLayout.Title
				text={text}
				voteCount={0}
				numberFormatter={numberFormatter}
				isExpanded={isExpanded}
				onToggle={() => setIsExpanded(x => !x)}
			/>
			<HeightAnimationContainer isVisible={isExpanded}>
				<VotersOptionLayout.NoVotes/>
			</HeightAnimationContainer>
		</VotersOptionLayout>
	);
};
OptionWithoutVotes.displayName = "OptionWithoutVotes";

type OptionProps = {
	postId: number;
	option: CommunityPostPollAnswerViewModel;
	voteCount: number;
	numberFormatter: NumberFormatter;
	trackingId: string;
};

const Option = (props: OptionProps): ReactElement => {
	const {
		postId,
		option,
		voteCount,
		numberFormatter,
		trackingId
	} = props;

	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [hasBeenExpanded, setHasBeenExpanded ] = useState<boolean>(false);

	const {
		id: optionId,
		text
	} = option;

	const {
		currentItems,
		getLoadingState,
		hasLazyLoadError,
		hasMoreOnServer,
		reload,
		retryToLoadLastPage
	} = useDataSource({
		globalContext: {
			postId,
			optionId,
			shouldFetchData: hasBeenExpanded
		},
		desiredAmountOfItems
	});

	const loadingState: RemoteDataSourceState = getLoadingState();

	const toggleHandler = useCallback(
		() => {
			setIsExpanded(x => !x);
			setHasBeenExpanded(true);
		},
		[]
	);

	const renderItems = (): ReactElement | null => {
		if (currentItems.length > 0 || loadingState === "loading") {
			return (
				<>
					<UserList
						items={currentItems}
						renderAfterItems={(
							<LoadMoreIndicator
								isLoadingData={loadingState === "loading"}
								hasMoreItems={hasMoreOnServer}
								onRequestMoreItems={() => setDesiredAmountOfItems(x => x + PAGE_SIZE)}
							/>
						)}
					/>
					{hasLazyLoadError && (
						<NoResults
							trackingId="not-able-to-retrieve-voters"
							icon="gear"
							title={localize("_UnableToRetrieveItems")}
							message={localize("_WeCantRetrieveAnyItemsAtThisMomentPleaseRetry")}
							onRetry={retryToLoadLastPage}
							hasExtraTopPadding
						/>
					)}
				</>
			);
		}

		if (loadingState === "error") {
			return (
				<NoResults
					trackingId="not-able-to-retrieve-voters"
					icon="gear"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeCantRetrieveAnyItemsAtThisMomentPleaseRetry")}
					onRetry={reload}
					height="fill"
				/>
			);
		}

		return (
			<VotersOptionLayout.NoVotes />
		);
	};

	return (
		<VotersOptionLayout trackingId={trackingId}>
			<VotersOptionLayout.Title
				isExpanded={isExpanded}
				onToggle={toggleHandler}
				text={text}
				voteCount={voteCount}
				numberFormatter={numberFormatter}
			/>
			<HeightAnimationContainer isVisible={isExpanded}>
				{renderItems()}
			</HeightAnimationContainer>
		</VotersOptionLayout>
	);
};
Option.displayName = "Option";

const PAGE_SIZE: number = 20;

type GlobalContex = {
	postId: number;
	optionId: number;
	shouldFetchData: boolean;
};

const useDataSource = createRemoteDataSource<UserIdentityModel, undefined, GlobalContex>()
	.setConfiguration({
		lazyLoadCursor: LazyLoadCursorKind.SkipTake
	})
	.getHook({
		performRequest: async (options): Promise<LazyLoadPageResult<UserIdentityModel>> => {
			const {
				globalContext,
				amount,
				skip,
				signal
			} = options;

			if (!globalContext.shouldFetchData) {
				return {
					hasMore: false,
					items: []
				};
			}

			const {
				optionId,
				postId
			} = globalContext;

			return listVoters(
				postId,
				optionId,
				{
					pageSize: amount,
					skip
				},
				signal
			);
		}
	});